import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useUser } from "@components/user-context";
import { useQueryMetadata } from "@components/v2/modals";
import {
  GET_ADOPTED_UNIVERSAL_BENCHMARKS_METADATA,
  GET_UNIVERSAL_BENCHMARKS_TARGETS,
} from "@gql/universal-bentchmarks";
import { useFeatureFlags, useNorthbeamQuery } from "@hooks/http";
import {
  type GetAdoptedUniversalBenchmarksMetadata,
  type GetUniversalBenchmarksTargets,
  type GetUniversalBenchmarksTargetsVariables,
} from "@north-beam/nb-client-side";

import {
  type BenchmarkTarget,
  type LoadAdoptedBenchmarksFuncArgs,
  type UniversalBenchmarksContextDescriptor,
  type UniversalBenchmarksQuery,
} from "./types";
import { buildBenchmarkKey } from "./utils";

export const UniversalBenchmarksContext =
  createContext<UniversalBenchmarksContextDescriptor | null>(null);

export const UniversalBenchmarksProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { user } = useUser();
  const { hasFeatureFlag } = useFeatureFlags();
  const { addQueryMetadata } = useQueryMetadata();

  const [queries, setQueries] = useState<Map<string, UniversalBenchmarksQuery>>(
    new Map(),
  );
  const [benchmarkTargets, setBenchmarkTargets] = useState<
    Map<string, BenchmarkTarget>
  >(new Map());

  const { data: metadata } =
    useNorthbeamQuery<GetAdoptedUniversalBenchmarksMetadata>(
      GET_ADOPTED_UNIVERSAL_BENCHMARKS_METADATA,
      { skip: !hasFeatureFlag("enableUniversalBenchmarks") },
    );

  const adoptedBenchmark = useMemo(() => {
    return metadata?.me.adoptedUniversalBenchmarksMetadata ?? null;
  }, [metadata]);

  const loadBenchmarkTargets = useCallback(
    (variables: GetUniversalBenchmarksTargetsVariables) => {
      return user.apolloClient.query<
        GetUniversalBenchmarksTargets,
        GetUniversalBenchmarksTargetsVariables
      >({
        query: GET_UNIVERSAL_BENCHMARKS_TARGETS,
        variables,
      });
    },
    [user.apolloClient],
  );

  const processedBenchmarkTargets = useRef<string[]>([]);

  useEffect(() => {
    if (!adoptedBenchmark) {
      return;
    }

    queries.forEach(async (query, key) => {
      if (processedBenchmarkTargets.current.includes(key)) {
        return;
      }

      processedBenchmarkTargets.current.push(key);

      setBenchmarkTargets((prev) => {
        prev.set(key, { status: "loading", targets: null });
        return new Map(prev);
      });

      const variables: GetUniversalBenchmarksTargetsVariables = {
        adoptedBenchmarkId: adoptedBenchmark.id,
        level: query.level,
        attributionModel: query.attributionModel,
        accountingMode: query.accountingMode,
        attributionWindow: query.attributionWindow,
        breakdowns: query.breakdowns,
        filters: query.filters,
      };

      const { data } = await loadBenchmarkTargets(variables);

      const benchmarksTargetsData =
        data.me.universalBenchmarksTargets.rows.reduce(
          (acc, e) => ({ ...acc, [e.dimensions]: e }),
          {},
        );

      addQueryMetadata({
        context: "Universal Benchmarks",
        topic: "Load Benchmark Targets",
        metadata: data.me.universalBenchmarksTargets.queryMetadata,
      });

      setBenchmarkTargets((prev) => {
        prev.set(key, {
          status: "done",
          targets: benchmarksTargetsData,
        });
        return new Map(prev);
      });
    });
  }, [adoptedBenchmark, queries]);

  const addQuery = useCallback(
    (query: UniversalBenchmarksQuery) => {
      if (!adoptedBenchmark) {
        return;
      }

      const key = buildBenchmarkKey(adoptedBenchmark.id, query);

      setQueries((prev) => {
        if (prev.has(key)) {
          return prev;
        } else {
          prev.set(key, query);
          return new Map(prev);
        }
      });
    },
    [adoptedBenchmark],
  );

  const loadAdoptedBenchmarks = useCallback(
    async ({
      level,
      attributionModel,
      attributionWindow,
      accountingMode,
      breakdowns,
      filters,
      metric,
    }: LoadAdoptedBenchmarksFuncArgs) => {
      if (!breakdowns) {
        return null;
      }

      if (!adoptedBenchmark) {
        return null;
      }

      if (!metric) {
        return null;
      }

      if (!adoptedBenchmark.selectedMetricIds.includes(metric.id)) {
        return null;
      }

      const adjustedAttributionWindow =
        accountingMode === "cash" || metric.isLtv
          ? "infinity"
          : attributionWindow;

      const { data } = await user.apolloClient.query<
        GetUniversalBenchmarksTargets,
        GetUniversalBenchmarksTargetsVariables
      >({
        query: GET_UNIVERSAL_BENCHMARKS_TARGETS,
        variables: {
          adoptedBenchmarkId: adoptedBenchmark.id,
          level,
          attributionModel,
          accountingMode,
          attributionWindow: adjustedAttributionWindow,
          breakdowns,
          filters,
        },
      });

      addQueryMetadata({
        context: "Universal Benchmarks",
        topic: "Load Benchmark Targets",
        metadata: data.me.universalBenchmarksTargets.queryMetadata,
      });

      const rv: Record<string, number> = {};

      for (const row of data.me.universalBenchmarksTargets.rows) {
        rv[row.dimensions] = row[metric.id];
      }

      return Object.keys(rv).length > 0 ? rv : null;
    },
    [user.apolloClient, adoptedBenchmark],
  );

  return (
    <UniversalBenchmarksContext.Provider
      value={{
        adoptedBenchmark,
        benchmarkTargets,
        addQuery,
        loadAdoptedBenchmarks,
      }}
    >
      {children}
    </UniversalBenchmarksContext.Provider>
  );
};
